import React, { useState, useRef, useEffect } from 'react';
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from '@/components/ui/table';
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, X, MoreHorizontal, InfoIcon } from 'lucide-react';
import { format } from 'date-fns';
import { AddNonMedical } from '@/components/add-non-medical';
import { AutoCompletePortal, Option } from '@/components/ui/autocomplete-portal'
import dayjs from 'dayjs';
import { AddedItemsSchema } from '@/routes/(pharma)/_layout/inventory/add-stock/-data/schema';
import { useDebounce } from '@uidotdev/usehooks';
import { useQuery } from '@connectrpc/connect-query';
import { searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery';
import { DiscountType, ItemType } from '@/gen/inventory/common_pb';
import { pharmacyClient } from '@/connect';
import { toast } from '../ui/use-toast';
import { TaxProfile } from '@/gen/medical_master/common_pb';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import BarcodeModal from '@/routes/(pharma)/_layout/inventory/add-stock/-components/BarcodeModal';
import getPharmacyID from '@/utils/pharmacy_code';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

function IndividualRow({
  item,
  idx,
  onChange,
  onDelete,
  totalLength,
}: {
  item: AddedItemsSchema;
  idx: number;
  onChange: (index: number, field: string, value: any) => void;
  onDelete: (index: number) => void;
  totalLength: number;
}) {
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [customBarcode, setCustomBarcode] = useState(item.metadata?.custom_barcode || '');
  const [discountType, setDiscountType] = useState(item.metadata?.discount_type || DiscountType.NONE.toString());
  const [discountValue, setDiscountValue] = useState(item.metadata?.discount_value || 0);
  const [note, setNote] = useState('');
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [generatedBarcodeData, setGeneratedBarcodeData] = useState<{
    barcode: string;
    productName: string;
    batchNumber: string;
    expiryDate: any;
    unit: string;
    unitType: string;
  } | null>(null);
  const [customBarcodeGenerating, setCustomBarcodeGenerating] = useState(false);
  const [expiryDateOpen, setExpiryDateOpen] = useState(false);
  const [productionDateOpen, setProductionDateOpen] = useState(false);
  const expiryDateButtonRef = useRef<HTMLButtonElement>(null);
  const productionDateButtonRef = useRef<HTMLButtonElement>(null);
  const originalQuantityInputRef = useRef<HTMLInputElement>(null);

  const [productOptions, setProductOptions] = useState<Option[]>(
    item.trade_name ? [{
      value: item.medical_master_id || item.non_medical_master_id || '',
      label: item.trade_name + (item.scientific_name ? ` - ${item.scientific_name}` : '') + ` (${item.unit} ${item.unit_type})`,
      trade_name: item.trade_name,
      type: item.type,
      medical_master_id: item.medical_master_id || "",
      non_medical_master_id: item.non_medical_master_id || "",
      taxProfile: item.taxProfile?.toJsonString() || "",
    }] : []
  );
  const [selectedProduct, setSelectedProduct] = useState<Option | undefined>(
    item.trade_name ? {
      value: item.medical_master_id || item.non_medical_master_id || '',
      label: item.trade_name + (item.scientific_name ? ` - ${item.scientific_name}` : '') + ` (${item.unit} ${item.unit_type})`,
      trade_name: item.trade_name,
      type: item.type,
      medical_master_id: item.medical_master_id || "",
      non_medical_master_id: item.non_medical_master_id || "",
      taxProfile: item.taxProfile?.toJsonString() || "",
    } : undefined
  );

  const [productSearchTerm, setProductSearchTerm] = useState('');

  const searchTerm = useDebounce(productSearchTerm, 500);

  const {data, error, isLoading} = useQuery(searchInventory, {
    query: searchTerm,
    limit: 10,
    onlyAvailable: false,
  })

  useEffect(() => {
    if (data && data.items.length > 0) {
      const options = data.items.map(item => ({
        value: item.id,
        label: item.tradeName + (item.scientificName ? ` - ${item.scientificName}` : '') + ` (${item.unit} ${item.unitType})`,
        trade_name: item.tradeName,
        type: item.type === ItemType.MEDICAL ? 'MEDICAL' : 'NON_MEDICAL',
        medical_master_id: item.type === ItemType.MEDICAL ? item.id : "",
        non_medical_master_id: item.type === ItemType.NON_MEDICAL ? item.id : "",
        taxProfile: item.taxProfile?.toJsonString() || "",
        scientific_name: item.scientificName || "",
        unit: item.unit.toString(),
        unit_type: item.unitType || "",
      }));
      setProductOptions(options);
    }
  }, [data, item.type, item.medical_master_id, item.non_medical_master_id]);

  useEffect(() => {
    if (item.trade_name) {
      setSelectedProduct({
        value: item.medical_master_id || item.non_medical_master_id || '',
        label: item.trade_name + (item.scientific_name ? ` - ${item.scientific_name}` : '') + ` (${item.unit} ${item.unit_type})`,
        trade_name: item.trade_name,
        type: item.type,
        medical_master_id: item.medical_master_id || "",
        non_medical_master_id: item.non_medical_master_id || "",
        taxProfile: item.taxProfile?.toJsonString() || "",
        scientific_name: item.scientific_name || "",
        unit: item.unit || "",
        unit_type: item.unit_type || "",
      });
      setProductSearchTerm(item.trade_name);
    } else {
      setSelectedProduct(undefined);
      setProductSearchTerm('');
    }
  }, [item.medical_master_id, item.non_medical_master_id, item.trade_name, item.type, item.taxProfile]);


  const handleGenerateBarcode = async () => {
    // Validation: Check batch number
    if (!item.batch_number || item.batch_number.trim() === '') {
      toast({
        title: "Batch Number Required",
        description: "Please enter a batch number before generating barcode",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
  
    // Validation: Check expiry date
    if (!item.expiry_date) {
      toast({
        title: "Expiry Date Required",
        description: "Please select an expiry date before generating barcode",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
  
    // Validation: Check product
    if (!item.medical_master_id && !item.non_medical_master_id) {
      toast({
        title: "Error",
        description: "Product information not available",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
  
    try {
      setCustomBarcodeGenerating(true);
      
      // Generate barcode
      const barcodeData = await pharmacyClient.generateCustomBarcode({
        masterId: item.medical_master_id || item.non_medical_master_id || '',
        itemType: item.type === 'MEDICAL' ? ItemType.MEDICAL : ItemType.NON_MEDICAL,
      });
      
      // Update form with generated barcode
      setCustomBarcode(barcodeData.barcode || '');
      
      // Prepare data for modal using form data
      setGeneratedBarcodeData({
        barcode: barcodeData.barcode as string,
        productName: item.trade_name || '',
        batchNumber: item.batch_number,
        expiryDate: item.expiry_date ? new Date(item.expiry_date) : new Date(),
        unit: '', // Can be extracted from product metadata if available
        unitType: '', // Can be extracted from product metadata if available
      });
      
      // Close details modal and open barcode modal
      setShowMoreModal(false);
      setBarcodeModalOpen(true);
      
    } catch (error) {
      console.error('Error generating barcode:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate custom barcode.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setCustomBarcodeGenerating(false);
    }
  };

  // Handler for product selection
  const handleProductSelect = (value: Option) => {
    if (value.type === 'MEDICAL') {
      onChange(idx, 'medical_master_id', value.value);
      onChange(idx, 'trade_name', value.trade_name || value.label);
      onChange(idx, 'non_medical_master_id', undefined);
      onChange(idx, 'type', 'MEDICAL');
      onChange(idx, 'taxProfile', TaxProfile.fromJsonString(value.taxProfile) || undefined);
      setPrice();
    } else {
      onChange(idx, 'non_medical_master_id', value.value);
      onChange(idx, 'trade_name', value.trade_name || value.label);
      onChange(idx, 'medical_master_id', undefined);
      onChange(idx, 'type', 'NON_MEDICAL');
      onChange(idx, 'taxProfile', TaxProfile.fromJsonString(value.taxProfile) || undefined);
    }
  };

  const setPrice = () => pharmacyClient.getPriceHistory({
      id: item.medical_master_id || '',
      page: 1,
      pageSize: 100
    }).then(response => {
      const price = response.priceHistories.length > 0 ? response.priceHistories.filter(item => item.isCurrentActivePriceForPharmacy).length > 0 ? response.priceHistories.filter(item => item.isCurrentActivePriceForPharmacy)[0].price : response.priceHistories[0].price : 0;
      if (price === undefined) {
        toast({
          title: 'Price Not Found',
          description: 'No current active price found for this medical item.',
          variant: 'destructive',
          duration: 3000,
        });
      } else {
        onChange(idx, 'price', price);
      }
    }).catch(err => {
      console.error('Error fetching price history:', err);
      toast({
        title: 'Error',
        description: 'Failed to fetch price history for this medical item.',
        variant: 'destructive',
        duration: 3000,
      });
  })

  useEffect(() => {
    console.log('Item changed:', item);
    if (item.type === 'MEDICAL' && item.medical_master_id) {
      setPrice();
    }
  }, [item]);

  // Handler for clearing product selection
  const handleClearProduct = () => {
    if (item.type === 'MEDICAL') {
      onChange(idx, 'medical_master_id', '');
    } else {
      onChange(idx, 'non_medical_master_id', '');
    }
    onChange(idx, 'trade_name', '');
  };

  return (
    <TableRow className="font-medium" key={idx}>
      <TableCell className={tableCellClass}>{idx + 1}</TableCell>
      <TableCell className={tableCellClass}>
        <AutoCompletePortal
          options={productOptions}
          value={selectedProduct}
          onValueChange={handleProductSelect}
          input={productSearchTerm}
          onInputChanged={setProductSearchTerm}
          emptyMessage="No products found."
          placeholder="Select product"
          isLoading={isLoading}
          disabled={false}
          onClear={handleClearProduct}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          value={item.batch_number}
          onChange={e => onChange(idx, 'batch_number', e.target.value)}
          placeholder="Batch Number"
          className="h-7 px-2"
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={expiryDateButtonRef}
              variant={"outline"}
              className={
                'w-full pl-3 text-left font-normal flex justify-between items-center ' +
                (!item.expiry_date ? 'text-muted-foreground' : '') +
                ' h-7 px-2'
              }
              onFocus={() => setExpiryDateOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && expiryDateOpen) {
                  e.preventDefault();
                  setExpiryDateOpen(false);
                  setTimeout(() => {
                    productionDateButtonRef.current?.focus();
                  }, 0);
                }
              }}
            >
              <span>{item.expiry_date ? format(new Date(item.expiry_date), 'PPP') : 'Expiry date'}</span>
              <CalendarIcon className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={item.expiry_date ? new Date(item.expiry_date) : undefined}
              fromDate={(() => {
                const today = dayjs().startOf('day');
                if (item.production_date) {
                  const productionDate = dayjs(item.production_date).startOf('day');
                  if (productionDate.isAfter(today)) {
                    return productionDate.toDate();
                  }
                }
                return today.toDate();
              })()}
              onSelect={date => {
                onChange(idx, 'expiry_date', date?.toString());
                setExpiryDateOpen(false);
                setTimeout(() => {
                  productionDateButtonRef.current?.focus();
                }, 0);
              }}
            />
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell className={tableCellClass}>
        <Popover open={productionDateOpen} onOpenChange={setProductionDateOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={productionDateButtonRef}
              variant={"outline"}
              className={
                'w-full pl-3 text-left font-normal flex justify-between items-center ' +
                (!item.production_date ? 'text-muted-foreground' : '') +
                ' h-7 px-2'
              }
              onFocus={() => setProductionDateOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && productionDateOpen) {
                  e.preventDefault();
                  setProductionDateOpen(false);
                  setTimeout(() => {
                    originalQuantityInputRef.current?.focus();
                  }, 0);
                }
              }}
            >
              <span>{item.production_date ? format(new Date(item.production_date), 'PPP') : 'Production date'}</span>
              <CalendarIcon className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={item.production_date ? new Date(item.production_date) : undefined}
              toDate={dayjs().startOf('day').toDate()}
              onSelect={date => {
                onChange(idx, 'production_date', date?.toString());
                setProductionDateOpen(false);
                setTimeout(() => {
                  originalQuantityInputRef.current?.focus();
                }, 0);
              }}
            />
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          ref={originalQuantityInputRef}
          type="number"
          value={item.quantity.original === 0 || item.quantity.original === undefined ? '' : item.quantity.original}
          onChange={e => onChange(idx, 'quantity.original', Number(e.target.value))}
          className="h-7 px-2"
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          value={item.quantity.bonus === 0 || item.quantity.bonus === undefined ? '' : item.quantity.bonus}
          onChange={e => onChange(idx, 'quantity.bonus', Number(e.target.value))}
          className="h-7 px-2"
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          value={item.cost === 0 || item.cost === undefined ? '' : item.cost}
          onChange={e => onChange(idx, 'cost', Number(e.target.value))}
          className="h-7 px-2 pr-8"
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <div className='flex items-center gap-2'>
          <Input
            type="number"
            disabled={item.type === 'MEDICAL'}
            value={item.price === 0 || item.price === undefined ? '' : item.price}
            onChange={e => onChange(idx, 'price', Number(e.target.value))}
            className="h-7 px-2 pr-8"
          />
          <Popover>
            <PopoverTrigger asChild>
              <InfoIcon width={15} height={15}/>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-700">
                  <p>
                    <b>Medical Items:</b> Price is auto-filled based on the current active price for the selected medical item.
                  </p>
                  <p>
                    <b>Non-Medical Items:</b> You can manually enter the price.
                  </p>
                </div>
                <div className="text-xs text-gray-500">
                  Tax Profile:
                  <span className="ml-1">
                    {item.taxProfile?.classificationCodes?.map(code => {
                      return (
                        <span key={code.code} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded mr-1">
                          {code.description} (via {code.source}) = {code.code}
                        </span>
                      );
                    })}
                    {item.taxProfile?.applicableTaxes?.map(tax => {
                      return (
                        <span key={tax.componentCode} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded mr-1">
                          {tax.componentCode} ({tax.description}) = {tax.ratePercent}%
                        </span>
                      );
                    })}
                  </span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </TableCell>
      <TableCell className={tableCellClass}>
        <div className="flex flex-row gap-2 items-center">
          <Button
            variant="outline"
            size="icon"
            className='h-7'
            onClick={() => setShowMoreModal(true)}
          >
            <MoreHorizontal className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => onDelete(idx)}
            className="text-gray-600 hover:text-red-500 hover:bg-red-100 transition-colors"
            aria-label="Delete row"
            disabled={totalLength <= 1 || 
              // Prevent deletion if it is the last row
              idx === totalLength - 1              
            }
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        {showMoreModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-4 w-96">
              <h3 className="font-semibold mb-2">Additional Item Details</h3>
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full border rounded p-2 mb-3 text-sm"
                  rows={3}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Enter notes for this item..."
                />
              </div>
              <div className="mb-2">
              <label className="block text-sm font-medium mb-1">Custom Barcode</label>
              <div className="flex gap-2">
                <Input
                  value={customBarcode}
                  onChange={e => setCustomBarcode(e.target.value)}
                  placeholder="Custom Barcode"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleGenerateBarcode}
                        disabled={customBarcodeGenerating}
                      >
                        {customBarcodeGenerating ? "Generating..." : "Generate & Print"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Batch number and expiry date required</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
              <div className="mb-2">
                <label className="block text-sm font-medium mb-1">Discount Type</label>
                <Select
                value={discountType}
                onValueChange={(value: string) => {
                  const discountType = value.toString();
                  if (discountType === DiscountType.NONE.toString()) {
                    setDiscountValue(0)
                  }
                  setDiscountType(discountType);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Discount Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={
                    DiscountType.NONE.toString()
                  }>None</SelectItem>
                  <SelectItem value={
                    DiscountType.PERCENTAGE.toString()
                  }>Percentage</SelectItem>
                  <SelectItem value={
                    DiscountType.FIXED.toString()
                  }>Fixed</SelectItem>
                  <SelectItem value={
                    DiscountType.ALWAYS_FIXED.toString()
                  }>Always Fixed</SelectItem>
                  <SelectItem value={
                    DiscountType.ALWAYS_PERCENTAGE.toString()
                  }>Always Percentage</SelectItem>
                </SelectContent>
              </Select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Discount Value</label>
                <Input
                  type="number"
                  value={discountValue}
                  onChange={e => setDiscountValue(Number(e.target.value))}
                  placeholder="Discount Value"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMoreModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    onChange(idx, 'metadata.custom_barcode', customBarcode);
                    onChange(idx, 'metadata.discount_type', discountType);
                    onChange(idx, 'metadata.discount_value', discountValue);
                    onChange(idx, 'metadata.note', note);
                    setShowMoreModal(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Barcode Modal */}
        {generatedBarcodeData && (
          <BarcodeModal
            open={barcodeModalOpen}
            onOpenChange={setBarcodeModalOpen}
            initialData={{
              pharmacyName: getPharmacyID(),
              productName: generatedBarcodeData.productName,
              expiryDate: generatedBarcodeData.expiryDate 
                ? dayjs(generatedBarcodeData.expiryDate).format("YYYY-MM-DD")
                : new Date().toISOString().split('T')[0],
              barcodeNumber: generatedBarcodeData.barcode,
            }}
            onPrintComplete={(barcodeNumber) => {
              toast({
                title: "Success",
                description: "Barcode printed successfully!",
                duration: 3000,
              });
              setBarcodeModalOpen(false);
              setGeneratedBarcodeData(null);
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

export default function PurchaseInvoiceTable({
  items,
  onChange,
  onDelete,
}: {
  items: AddedItemsSchema[];
  onChange: (index: number, field: string, value: any) => void;
  onDelete: (index: number) => void;
}) {
  console.log('Rendering PurchaseInvoiceTable with items:', items);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-auto flex flex-col">
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className={tableHeadClass + ''}>SL NO.</TableHead>
              <TableHead className={tableHeadClass + ' min-w-[350px]'}>
                <div className="flex items-center justify-between gap-2">
                  Product Name
                  <div className='text-gray-700'>
                    <AddNonMedical />
                  </div>
                </div>
              </TableHead>
              <TableHead className={tableHeadClass}>Batch Number</TableHead>
              <TableHead className={tableHeadClass}>Expiry Date</TableHead>
              <TableHead className={tableHeadClass}>Production Date</TableHead>
              <TableHead className={tableHeadClass}>Original</TableHead>
              <TableHead className={tableHeadClass}>Bonus</TableHead>
              <TableHead className={tableHeadClass}>Cost (Excl Tax)</TableHead>
              <TableHead className={tableHeadClass}>Price (Excl Tax)</TableHead>
              <TableHead className={tableHeadClass}></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, idx) => (
              <IndividualRow
                key={idx}
                item={item}
                idx={idx}
                onChange={onChange}
                onDelete={onDelete}
                totalLength={items.length}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 